<template>
    <transition appear enter-active-class="animated bounceInRight">
        <f7-view main>
            <f7-page :page-content="false">
                <cm-navbar :title="$ml.get('wasteOutOrders')">
                    <f7-nav-right>
                        <f7-link @click="refresh"><i class="f7-icons">refresh</i></f7-link>
                    </f7-nav-right>
                </cm-navbar>

                <f7-toolbar tabbar bottom>
                    <f7-link tab-link="#waste-out-create" tab-link-active>{{ $ml.get('wasteOutOrders') }}</f7-link>
                    <f7-link tab-link="#waste-out-recent">{{ $ml.get('wasteOutRecent') }}</f7-link>
                </f7-toolbar>

                <f7-tabs animated>
                    <f7-tab id="waste-out-create" class="page-content" tab-active>
                        <template v-if="canCreate">
                        <f7-list inline-labels no-hairlines-md>
                            <f7-list-input type="select" :label="$ml.get('wasteOutKind')" :value="kind" :disabled="loadingSources" @change="changeKind($event.target.value)">
                                <option v-for="option in kinds" :key="option.value" :value="option.value">{{ $ml.get(option.label) }}</option>
                            </f7-list-input>
                        </f7-list>
                        <f7-block-title>{{ $ml.get(sourceTitleKey) }}</f7-block-title>
                        <f7-list inline-labels no-hairlines-md>
                            <f7-list-input
                                type="text"
                                :label="$ml.get('wasteOutSearch')"
                                :placeholder="$ml.get(sourceSearchKey)"
                                clear-button
                                :value="sourceSearch"
                                @input="sourceSearch = $event.target.value"
                                @keyup.enter="searchSources"
                            ></f7-list-input>
                            <template v-if="kind === 'finished'">
                                <f7-list-input type="date" :label="$ml.get('fromDate')" :value="sourceFromDate" @input="sourceFromDate = $event.target.value"></f7-list-input>
                                <f7-list-input type="date" :label="$ml.get('toDate')" :value="sourceToDate" @input="sourceToDate = $event.target.value"></f7-list-input>
                            </template>
                        </f7-list>
                        <f7-block>
                            <f7-button fill @click="searchSources" :disabled="loadingSources">
                                {{ $ml.get('wasteOutSearch') }}
                            </f7-button>
                        </f7-block>

                        <f7-block v-if="isByproduct" class="waste-out-stock-note">{{ $ml.get('wasteOutByproductHint') }}</f7-block>
                        <f7-block v-if="selectedByproductMaterial">
                            <p>{{ $ml.get('wasteOutByproduct') }}: {{ materialName(selectedByproductMaterial) }}</p>
                            <f7-button outline @click="changeByproductMaterial" :disabled="loadingSources">{{ $ml.get('wasteOutChangeMaterial') }}</f7-button>
                        </f7-block>
                        <f7-block v-if="loadingSources" class="text-align-center">
                            <f7-preloader></f7-preloader>
                        </f7-block>
                        <f7-list v-else-if="isByproduct && !selectedByproductMaterial">
                            <f7-list-item
                                v-for="material in byproductMaterials"
                                :key="material.id"
                                :title="material.name"
                                :after="$ml.get('wasteOutChooseContainer')"
                                class="waste-out-to-item"
                                @click="selectByproductMaterial(material)"
                            ></f7-list-item>
                        </f7-list>
                        <f7-list media-list v-else-if="isByproduct">
                            <f7-list-item
                                v-for="container in sources"
                                :key="container.id"
                                :title="container.name"
                                :subtitle="container.inventory ? container.inventory.name : ''"
                                :after="amountText(container.capacity)"
                                class="waste-out-to-item"
                                @click="selectByproductContainer(container)"
                            ></f7-list-item>
                        </f7-list>
                        <template v-else-if="kind === 'finished'">
                            <f7-block class="waste-out-stock-note">{{ $ml.get('wasteOutStockHint') }}</f7-block>
                            <f7-card v-for="item in sources" :key="item.id" class="waste-out-mo-card">
                                <f7-card-header>{{ materialName(item.material) }}</f7-card-header>
                                <f7-card-content>
                                    <p class="waste-out-mo-meta">{{ sourceLabel(item) }} · {{ item.created_at }}</p>
                                    <p>{{ $ml.get('wasteOutProductionAmount') }}: {{ amountText(item.amount) }}</p>
                                    <div v-for="container in item.target_containers" :key="container.id" class="waste-out-mo-location">
                                        <p>{{ container.name }}</p>
                                        <p><strong>{{ $ml.get('wasteOutLocationStock') }}: {{ amountText(container.capacity) }}</strong></p>
                                        <p v-if="containerUnavailableReason(container, item)" class="waste-out-unavailable">{{ containerUnavailableReason(container, item) }}</p>
                                    </div>
                                    <p v-if="!item.target_containers || !item.target_containers.length" class="waste-out-unavailable">{{ $ml.get('wasteOutNoTargetContainers') }}</p>
                                </f7-card-content>
                                <f7-card-footer>
                                    <f7-button fill :disabled="!availableSourceContainers(item).length" @click="selectSource(item)">{{ $ml.get('wasteOutSelectMo') }}</f7-button>
                                </f7-card-footer>
                            </f7-card>
                        </template>
                        <f7-list media-list v-else>
                            <f7-list-item
                                v-for="item in sources"
                                :key="item.id"
                                :title="sourceLabel(item)"
                                :subtitle="sourceSubtitle(item)"
                                :text="sourceTargetContainers(item)"
                                :after="item.created_at || ''"
                                class="waste-out-to-item"
                                @click="selectSource(item)"
                            ></f7-list-item>
                        </f7-list>
                        <f7-card v-if="!loadingSources && !sources.length" :content="$ml.get('noRecords')"></f7-card>
                        <f7-block v-if="!loadingSources && sources.length >= 20" class="waste-out-stock-note">{{ $ml.get('wasteOutSearchMore') }}</f7-block>
                        </template>
                        <f7-block v-else class="waste-out-permission-message">
                            {{ $ml.get('wasteOutNoCreatePermission') }}
                        </f7-block>
                    </f7-tab>

                    <f7-tab id="waste-out-recent" class="page-content">
                        <f7-block-title>{{ $ml.get('wasteOutRecent') }}</f7-block-title>
                        <f7-list inline-labels no-hairlines-md>
                            <f7-list-input type="select" :label="$ml.get('wasteOutKind')" :value="recentKind" @change="recentKind = $event.target.value">
                                <option value="">{{ $ml.get('wasteOutAllKinds') }}</option>
                                <option v-for="option in kinds" :key="option.value" :value="option.value">{{ $ml.get(option.label) }}</option>
                            </f7-list-input>
                            <f7-list-input
                                type="text"
                                :label="$ml.get('wasteOutSearch')"
                                :placeholder="$ml.get('wasteOutSearch')"
                                clear-button
                                :value="wasteOutSearch"
                                @input="wasteOutSearch = $event.target.value"
                                @keyup.enter="loadWasteOuts"
                            ></f7-list-input>
                            <f7-list-input
                                type="date"
                                :label="$ml.get('fromDate')"
                                :value="fromDate"
                                @input="fromDate = $event.target.value"
                            ></f7-list-input>
                            <f7-list-input
                                type="date"
                                :label="$ml.get('toDate')"
                                :value="toDate"
                                @input="toDate = $event.target.value"
                            ></f7-list-input>
                        </f7-list>
                        <f7-block>
                            <f7-button fill @click="loadWasteOuts" :disabled="loadingWasteOuts">
                                {{ $ml.get('wasteOutSearch') }}
                            </f7-button>
                        </f7-block>

                        <f7-block v-if="loadingWasteOuts" class="text-align-center">
                            <f7-preloader></f7-preloader>
                        </f7-block>
                        <template v-else>
                            <f7-card v-for="item in wasteOuts" :key="item.id" class="waste-out-receipt">
                                <f7-card-header>
                                    <span>{{ item.code || ('#' + item.id) }}</span>
                                    <span :class="receiptStatusClass(item)">{{ receiptStatus(item) }}</span>
                                </f7-card-header>
                                <f7-card-content>
                                    <p>{{ $ml.get('wasteOutKind') }}: {{ kindLabel(item.kind) }}</p>
                                    <p v-if="receiptSourceLabel(item)">{{ $ml.get('wasteOutSource') }}: {{ receiptSourceLabel(item) }}</p>
                                    <p>{{ $ml.get('containers') }}: {{ item.container ? item.container.name : '' }}</p>
                                    <p>{{ $ml.get('wasteOutOutputMaterial') }}: {{ materialName(item.material || (item.container && item.container.material)) }}</p>
                                    <p>{{ $ml.get('wasteOutAmount') }}: {{ amountText(item.amount) }}</p>
                                    <p>{{ $ml.get('wasteOutNote') }}: {{ item.note }}</p>
                                    <p v-if="item.createdBy">{{ $ml.get('wasteOutCreatedBy') }}: {{ item.createdBy.name }}</p>
                                    <p v-if="item.created_at">{{ item.created_at }}</p>
                                    <p v-if="item.cancel_note">{{ $ml.get('wasteOutCancelNote') }}: {{ item.cancel_note }}</p>
                                </f7-card-content>
                                <f7-card-footer v-if="canCancelReceipt(item)">
                                    <span></span>
                                    <f7-link color="red" @click="askCancel(item)">{{ $ml.get('wasteOutCancel') }}</f7-link>
                                </f7-card-footer>
                            </f7-card>
                            <f7-card v-if="!wasteOuts.length" :content="$ml.get('noRecords')"></f7-card>
                        </template>
                    </f7-tab>
                </f7-tabs>

                <f7-popup
                    class="waste-out-popup"
                    :opened="popupOpened"
                    tablet-fullscreen
                    @popup:closed="closePopup"
                >
                    <f7-view>
                        <f7-page>
                            <f7-navbar :title="$ml.get('wasteOutOrders')">
                                <f7-nav-right>
                                    <f7-link popup-close>{{ $ml.get('close') }}</f7-link>
                                </f7-nav-right>
                            </f7-navbar>

                            <template v-if="selectedSource || (isByproduct && selectedContainer)">
                                <f7-block-title>{{ kindLabel(kind) }}</f7-block-title>
                                <f7-list inline-labels no-hairlines-md>
                                    <f7-list-item v-if="selectedSource" :title="$ml.get('wasteOutSource')" :after="sourceLabel(selectedSource)"></f7-list-item>
                                    <f7-list-item :title="$ml.get('wasteOutOutputMaterial')" :after="materialName(selectedMaterial)"></f7-list-item>
                                    <f7-list-item v-if="selectedSource" :title="$ml.get(kind === 'finished' ? 'wasteOutProductionAmount' : 'wasteOutOutputAmount')" :after="amountText(sourceAmount(selectedSource))"></f7-list-item>
                                </f7-list>

                                <f7-block v-if="!isByproduct" class="waste-out-stock-note">{{ $ml.get('wasteOutStockHint') }}</f7-block>
                                <f7-block-title v-if="!isByproduct">{{ $ml.get('wasteOutTargetContainer') }}</f7-block-title>
                                <f7-list v-if="!isByproduct && targetContainers.length">
                                    <f7-list-item
                                        v-for="container in targetContainers"
                                        :key="container.id"
                                        radio
                                        name="waste-out-target-container"
                                        :value="container.id"
                                        :title="container.name"
                                        :subtitle="kind === 'finished' ? (containerUnavailableReason(container, selectedSource) || materialName(container.material)) : materialName(container.material)"
                                        :after="amountText(container.capacity)"
                                        :disabled="kind === 'finished' && !!containerUnavailableReason(container, selectedSource)"
                                        :checked="isSelectedContainer(container)"
                                        @change="selectContainer(container)"
                                    ></f7-list-item>
                                </f7-list>
                                <f7-card v-else-if="!isByproduct" :content="$ml.get('wasteOutNoTargetContainers')"></f7-card>

                                <template v-if="selectedContainer">
                                    <f7-block-title>{{ $ml.get('basicInfo') }}</f7-block-title>
                                    <f7-list inline-labels no-hairlines-md>
                                        <f7-list-item :title="$ml.get('containers')" :after="selectedContainer.name"></f7-list-item>
                                        <f7-list-item :title="$ml.get('wasteOutOutputMaterial')" :after="materialName(selectedMaterial)"></f7-list-item>
                                        <f7-list-item :title="$ml.get('wasteOutCurrentStock')" :after="amountText(currentStock)"></f7-list-item>
                                        <f7-list-input
                                            type="number"
                                            name="waste-out-amount"
                                            min="0"
                                            step="any"
                                            :label="$ml.get('wasteOutAmount')"
                                            placeholder="0"
                                            clear-button
                                            :value="form.amount"
                                            @input="form.amount = $event.target.value"
                                        ></f7-list-input>
                                        <f7-list-input
                                            type="textarea"
                                            name="waste-out-note"
                                            :label="$ml.get('wasteOutNote')"
                                            :placeholder="$ml.get('wasteOutNote')"
                                            clear-button
                                            :value="form.note"
                                            @input="form.note = $event.target.value"
                                        ></f7-list-input>
                                    </f7-list>
                                    <f7-block>
                                        <f7-row>
                                            <f7-col>
                                                <f7-button outline @click="fillAllRemaining" :disabled="currentStock <= 0">
                                                    {{ $ml.get('wasteOutAllRemaining') }}
                                                </f7-button>
                                            </f7-col>
                                            <f7-col>
                                                <f7-button fill @click="confirmCreate" :disabled="submitting">
                                                    {{ $ml.get('wasteOutCreate') }}
                                                </f7-button>
                                            </f7-col>
                                        </f7-row>
                                    </f7-block>
                                </template>
                            </template>
                        </f7-page>
                    </f7-view>
                </f7-popup>
            </f7-page>
        </f7-view>
    </transition>
</template>

<script>
    import CMNavbar from './Navbar'
    import Query    from '@/services/api/Query'
    import Mutation from '@/services/api/Mutation'

    export default {
        name: 'WasteOut',
        components: {
            'cm-navbar': CMNavbar,
        },
        data() {
            return {
                kinds: [
                    {value: 'semi_finished', label: 'wasteOutSemiFinished'},
                    {value: 'finished', label: 'wasteOutFinished'},
                    {value: 'byproduct', label: 'wasteOutByproduct'},
                ],
                kind: 'semi_finished',
                recentKind: '',
                sources: [],
                wasteOuts: [],
                sourceSearch: '',
                sourceFromDate: '',
                sourceToDate: '',
                wasteOutSearch: '',
                fromDate: '',
                toDate: '',
                selectedSource: null,
                selectedByproductMaterial: null,
                selectedContainer: null,
                form: {
                    amount: '',
                    note: '',
                },
                loadingSources: false,
                loadingWasteOuts: false,
                submitting: false,
                popupOpened: false,
            }
        },
        computed: {
            isByproduct() {
                return this.kind === 'byproduct'
            },
            sourceTitleKey() {
                if (this.isByproduct) return this.selectedByproductMaterial ? 'wasteOutChooseContainer' : 'wasteOutChooseByproduct'
                return this.kind === 'finished' ? 'wasteOutChooseMo' : 'wasteOutChooseTo'
            },
            sourceSearchKey() {
                if (this.isByproduct) return 'wasteOutSearchByproduct'
                return this.kind === 'finished' ? 'wasteOutSearchMo' : 'wasteOutSearchTo'
            },
            byproductMaterials() {
                let materials = []
                this.sources.forEach(container => {
                    if (container.material && !materials.some(material => String(material.id) === String(container.material.id)))
                        materials.push(container.material)
                })
                return materials
            },
            targetContainers() {
                return this.selectedSource && Array.isArray(this.selectedSource.target_containers)
                    ? this.selectedSource.target_containers
                    : []
            },
            selectedMaterial() {
                if (this.isByproduct) return this.selectedContainer ? this.selectedContainer.material : null
                if (!this.selectedSource) return null
                // Keep the source output material even if a silo has been reused.
                // The API validates its current material before changing stock.
                return this.kind === 'finished' ? this.selectedSource.material : this.selectedSource.tmaterial
            },
            selectedMaterialId() {
                if (this.isByproduct) return this.selectedMaterial ? this.selectedMaterial.id : null
                if (!this.selectedSource) return null
                return this.kind === 'finished' ? this.selectedSource.material_id : this.selectedSource.to_material
            },
            currentStock() {
                return this.toNumber(this.selectedContainer ? this.selectedContainer.capacity : 0)
            },
            canCreate() {
                return this.$helper.hasPermission('waste_out_create')
            },
            canCancel() {
                return this.$helper.hasPermission('waste_out_cancel')
            },
        },
        methods: {
            toNumber(value) {
                let number = Number(value)

                return Number.isFinite(number) ? number : 0
            },

            amountText(value) {
                return this.$helper.numFormat(this.toNumber(value)) + ' kg'
            },

            materialName(material) {
                return material && material.name ? material.name : ''
            },

            kindLabel(kind) {
                let option = this.kinds.find(option => option.value === (kind || 'semi_finished'))
                return option ? this.$ml.get(option.label) : kind
            },

            sourceLabel(item) {
                if (!item) return ''

                return item.code || ((this.kind === 'finished' ? 'MO #' : 'TO #') + item.id)
            },

            sourceAmount(item) {
                return this.kind === 'finished' ? item.amount : item.to_amount
            },

            sourceSubtitle(item) {
                let material = this.materialName(this.kind === 'finished' ? item.material : item.tmaterial)
                let amount = this.amountText(this.sourceAmount(item))

                return material ? material + ' · ' + amount : amount
            },

            sourceTargetContainers(item) {
                let containers = Array.isArray(item.target_containers) ? item.target_containers : []

                return containers.map(container => container.name).join(', ')
            },

            containerUnavailableReason(container, source) {
                if (!container || this.toNumber(container.capacity) <= 0) return this.$ml.get('wasteOutEmptyLocation')
                if (!source || !container.material || String(container.material.id) !== String(source.material_id))
                    return this.$ml.get('wasteOutOtherMaterial') + (container.material ? ': ' + this.materialName(container.material) : '')
                return ''
            },

            availableSourceContainers(source) {
                return (source.target_containers || []).filter(container => !this.containerUnavailableReason(container, source))
            },

            isSelectedContainer(container) {
                return this.selectedContainer && String(this.selectedContainer.id) === String(container.id)
            },

            async changeKind(kind) {
                if (this.loadingSources || !this.kinds.some(option => option.value === kind)) return
                this.closePopup()
                this.kind = kind
                this.sourceSearch = ''
                this.sourceFromDate = ''
                this.sourceToDate = ''
                this.selectedByproductMaterial = null
                this.sources = []
                await this.loadSources()
            },

            async selectByproductMaterial(material) {
                if (this.loadingSources) return
                this.selectedByproductMaterial = material
                await this.loadSources()
            },

            async changeByproductMaterial() {
                if (this.loadingSources) return
                this.selectedByproductMaterial = null
                await this.loadSources()
            },

            selectByproductContainer(container) {
                this.resetCreateForm()
                this.selectedContainer = container
                this.popupOpened = true
            },

            selectSource(item) {
                if (this.kind === 'finished' && !this.availableSourceContainers(item).length) return
                this.selectedSource = item
                let containers = Array.isArray(item.target_containers) ? item.target_containers : []
                if (this.kind === 'finished') containers = this.availableSourceContainers(item)

                this.selectedContainer = containers.length === 1 ? containers[0] : null
                this.form.amount = ''
                this.form.note = ''
                this.popupOpened = true
            },

            resetCreateForm() {
                this.selectedSource = null
                this.selectedContainer = null
                this.form.amount = ''
                this.form.note = ''
            },

            closePopup() {
                this.popupOpened = false
                this.resetCreateForm()
            },

            selectContainer(container) {
                if (this.kind === 'finished' && this.containerUnavailableReason(container, this.selectedSource)) return
                this.selectedContainer = container
                this.form.amount = ''
            },

            fillAllRemaining() {
                this.form.amount = String(this.currentStock)
            },

            canCancelReceipt(item) {
                return this.canCancel && item && item.can_cancel === true
            },

            receiptStatus(item) {
                return item.status_label || item.status || ''
            },

            receiptStatusClass(item) {
                return Number(item.status) === 2 || String(item.status || '').toLowerCase() === 'cancelled'
                    ? 'waste-out-status-cancelled'
                    : 'waste-out-status-approved'
            },

            receiptSourceLabel(item) {
                if (item.kind === 'byproduct') return ''
                if (item.kind === 'finished') return item.mo_id ? 'MO #' + item.mo_id : ''
                let toId = item.to_id || (item.to && item.to.id)

                return toId ? 'TO #' + toId : ''
            },

            errorMessage(error) {
                let errors = error && error.data && error.data.errors

                if (Array.isArray(errors) && errors.length)
                    return this.$helper.errors(errors)

                if (error && error.response && error.response.data && error.response.data.message)
                    return error.response.data.message

                return error && error.message ? error.message : this.$ml.get('errors.title')
            },

            showError(error) {
                return this.$f7.dialog.alert(this.errorMessage(error), this.$ml.get('errors.title'))
            },

            async searchSources() {
                await this.loadSources()
            },

            async loadSources() {
                if (!this.canCreate || this.loadingSources) return
                if (this.kind === 'finished' && this.sourceFromDate && this.sourceToDate && this.sourceFromDate > this.sourceToDate)
                    return this.$f7.dialog.alert(this.$ml.get('wasteOutInvalidDates'), this.$ml.get('errors.title'))

                this.loadingSources = true
                this.sources = []
                try {
                    let request
                    let field
                    if (this.isByproduct) {
                        let materialId = this.selectedByproductMaterial ? this.selectedByproductMaterial.id : null
                        request = Query.withWasteOutByproducts(this.sourceSearch.trim(), materialId, 20)
                        field = 'waste_out_byproducts'
                    } else if (this.kind === 'finished') {
                        request = Query.withWasteOutMos(this.sourceSearch.trim(), 20, {from_date: this.sourceFromDate, to_date: this.sourceToDate})
                        field = 'waste_out_mos'
                    } else {
                        request = Query.withWasteOutTos(this.sourceSearch.trim(), 20)
                        field = 'waste_out_tos'
                    }
                    let result = await request.get()
                    if (result.data.errors) return this.showError(result)

                    let sources = this.$helper.data(result, field)
                    this.sources = Array.isArray(sources) ? sources : []
                    if (this.selectedSource) {
                        this.selectedSource = this.sources.find(item => String(item.id) === String(this.selectedSource.id)) || null
                        if (!this.selectedSource) this.closePopup()
                    }
                    if (this.selectedContainer) {
                        let containers = this.isByproduct ? this.sources : this.targetContainers
                        this.selectedContainer = containers.find(item => String(item.id) === String(this.selectedContainer.id)) || null
                        if (this.kind === 'finished' && this.selectedContainer && this.containerUnavailableReason(this.selectedContainer, this.selectedSource)) this.selectedContainer = null
                        if (this.isByproduct && !this.selectedContainer) this.closePopup()
                    }
                } catch (error) {
                    this.showError(error)
                } finally {
                    this.loadingSources = false
                }
            },

            async loadWasteOuts() {
                if (this.loadingWasteOuts) return
                this.loadingWasteOuts = true
                try {
                    let result = await Query.withWasteOuts(this.wasteOutSearch.trim(), 20, {
                        from_date: this.fromDate,
                        to_date: this.toDate,
                        kind: this.recentKind,
                    }).get()
                    if (result.data.errors) return this.showError(result)

                    this.wasteOuts = this.$helper.data(result, 'waste_outs')
                } catch (error) {
                    this.showError(error)
                } finally {
                    this.loadingWasteOuts = false
                }
            },

            async refresh() {
                // Query is a legacy fluent builder with a shared query buffer.
                // Keep these requests sequential so their GraphQL bodies cannot mix.
                if (this.canCreate) await this.loadSources()
                await this.loadWasteOuts()
            },

            validateCreate() {
                let amount = this.toNumber(this.form.amount)
                let note = (this.form.note || '').trim()

                if ((!this.isByproduct && !this.selectedSource) || !this.selectedContainer || !this.selectedMaterialId)
                    return false
                if (this.kind === 'finished' && this.containerUnavailableReason(this.selectedContainer, this.selectedSource)) return false
                if (amount <= 0) {
                    this.$f7.dialog.alert(this.$ml.get('errors.greaterZero'), this.$ml.get('errors.title'))
                    return false
                }
                if (amount > this.currentStock) {
                    this.$f7.dialog.alert(this.$ml.get('errors.wasteOutAmountOverStock'), this.$ml.get('errors.title'))
                    return false
                }
                if (!note) {
                    this.$f7.dialog.alert(this.$ml.get('errors.wasteOutNoteRequired'), this.$ml.get('errors.title'))
                    return false
                }

                return true
            },

            confirmCreate() {
                if (!this.validateCreate()) return

                let amount = this.toNumber(this.form.amount)
                let before = this.currentStock
                let after = before - amount
                let text = '<p>' + this.$ml.get('wasteOutBefore') + ': <b>' + this.amountText(before) + '</b></p>' +
                    '<p>' + this.$ml.get('wasteOutAmount') + ': <b>' + this.amountText(amount) + '</b></p>' +
                    '<p>' + this.$ml.get('wasteOutAfter') + ': <b>' + this.amountText(after) + '</b></p>'

                this.$f7.dialog.confirm(text, this.$ml.get('wasteOutConfirm'), () => this.create())
            },

            async create() {
                if (this.submitting || !this.validateCreate()) return

                this.submitting = true
                this.$f7.dialog.preloader(this.$ml.get('processing'))
                let isPreloaderOpen = true
                try {
                    let result = await Mutation.createWasteOut({
                        kind: this.kind,
                        to_id: this.kind === 'semi_finished' && this.selectedSource ? this.selectedSource.id : null,
                        mo_id: this.kind === 'finished' && this.selectedSource ? this.selectedSource.id : null,
                        container_id: this.selectedContainer.id,
                        material_id: this.selectedMaterialId,
                        amount: this.toNumber(this.form.amount),
                        note: this.form.note.trim(),
                    }).execute()

                    if (result.data.errors) {
                        this.$f7.dialog.close()
                        isPreloaderOpen = false
                        return this.showError(result)
                    }

                    this.$f7.dialog.close()
                    isPreloaderOpen = false
                    this.closePopup()
                    await this.refresh()
                    this.$f7.dialog.alert(this.$ml.get('success.createWasteOut'), this.$ml.get('information'))
                } catch (error) {
                    if (isPreloaderOpen) this.$f7.dialog.close()
                    isPreloaderOpen = false
                    this.showError(error)
                } finally {
                    this.submitting = false
                    if (isPreloaderOpen) this.$f7.dialog.close()
                }
            },

            askCancel(item) {
                this.$f7.dialog.prompt(
                    this.$ml.get('wasteOutCancelNote'),
                    this.$ml.get('wasteOutCancel'),
                    (note) => {
                        if (!(note || '').trim()) {
                            return this.$f7.dialog.alert(
                                this.$ml.get('errors.wasteOutCancelNoteRequired'),
                                this.$ml.get('errors.title'),
                                () => this.askCancel(item)
                            )
                        }

                        return this.cancel(item, note.trim())
                    }
                )
            },

            async cancel(item, note) {
                this.$f7.dialog.preloader(this.$ml.get('processing'))
                let isPreloaderOpen = true
                try {
                    let result = await Mutation.cancelWasteOut({
                        id: item.id,
                        cancel_note: note,
                    }).execute()

                    if (result.data.errors) {
                        this.$f7.dialog.close()
                        isPreloaderOpen = false
                        return this.showError(result)
                    }

                    this.$f7.dialog.close()
                    isPreloaderOpen = false
                    await this.refresh()
                    this.$f7.dialog.alert(this.$ml.get('success.cancelWasteOut'), this.$ml.get('information'))
                } catch (error) {
                    if (isPreloaderOpen) this.$f7.dialog.close()
                    isPreloaderOpen = false
                    this.showError(error)
                } finally {
                    if (isPreloaderOpen) this.$f7.dialog.close()
                }
            },
        },
        async created() {
            let requestedKind = this.$route && this.$route.query && this.$route.query.kind
            if (this.kinds.some(option => option.value === requestedKind)) this.kind = requestedKind
            await this.refresh()
        },
    }
</script>

<style scoped>
    .waste-out-mo-card .card-header {
        font-weight: 600;
    }

    .waste-out-mo-card p {
        margin: 0 0 6px;
    }

    .waste-out-mo-meta {
        color: #666;
        font-size: 13px;
    }

    .waste-out-mo-location {
        border-top: 1px solid #e5e5ea;
        margin-top: 10px;
        padding-top: 10px;
    }

    .waste-out-unavailable {
        color: #b42318;
        font-size: 13px;
    }

    .waste-out-to-item {
        cursor: pointer;
    }

    .waste-out-permission-message,
    .waste-out-stock-note {
        color: #8e8e93;
        font-size: 13px;
    }

    .waste-out-receipt p {
        margin: 0 0 8px;
    }

    .waste-out-receipt p:last-child {
        margin-bottom: 0;
    }

    .waste-out-status-approved,
    .waste-out-status-cancelled {
        border-radius: 12px;
        color: #fff;
        font-size: 12px;
        padding: 3px 8px;
    }

    .waste-out-status-approved {
        background: #34c759;
    }

    .waste-out-status-cancelled {
        background: #8e8e93;
    }
</style>
